import { getActiveDowntimes } from "./downtimes";
import { CalendarProjectName } from "./CalendarJobBox";
import ApiService from "@/api/ApiService";
import LabelWrapper from "@/components/root/LabelWrapper";
import NewRangePicker from "@/shared/forms/NewRangePicker";
import {
  AccountCircle,
  Call,
  ChatBubble,
  Check,
  Delete,
  DoneAll,
  Edit,
  Info,
  LocalShipping,
  People,
  PeopleAlt,
  Search,
  Signpost,
  Tag,
  ThumbDown,
  Warning,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Dialog, DialogTitle, DialogContent, TextField, DialogActions, Button, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate, useNavigation } from "react-router-dom";
import { Fragment } from "react/jsx-runtime";

export const Deleter = ({ date, bookingId, deleteMutation }) => {
  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState(date);
  const [endDate, setEndDate] = useState(date);

  return (
    <Fragment>
      <div onClick={() => setOpen(true)} className="resOrderButton red">
        <Delete fontSize="inherit" />
      </div>
      {open ? (
        <Dialog open={true} maxWidth="sm" onClose={() => setOpen(false)}>
          <DialogTitle>Buchung löschen</DialogTitle>
          <DialogContent>
            <NewRangePicker
              small
              onChange={([startDate, endDate]) => {
                setStartDate(!!startDate ? startDate.format("YYYY-MM-DD") : null);
                setEndDate(!!endDate ? endDate.format("YYYY-MM-DD") : null);
              }}
              value={[dayjs(startDate, "YYYY-MM-DD"), dayjs(endDate, "YYYY-MM-DD")]}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                setOpen(false);
                deleteMutation.mutate({
                  id: bookingId,
                  start: startDate,
                  end: endDate,
                });
              }}
            >
              Löschen
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}
    </Fragment>
  );
};

export const Rejector = ({ date, orderId, referenceType, referenceId, changeMutation }) => {
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [sendMail, setSendMail] = useState(false);
  const [startDate, setStartDate] = useState(date);
  const [endDate, setEndDate] = useState(date);

  const [waitForSuccess, setWaitForSuccess] = useState(false);

  useEffect(() => {
    if (!changeMutation.isPending && waitForSuccess) {
      setOpen(false);
      setWaitForSuccess(false);
      setComment("");
    }
  }, [changeMutation.isPending]);

  return (
    <Fragment>
      <div className="resOrderButton red" onClick={() => setOpen(true)}>
        <ThumbDown fontSize="inherit" />
      </div>
      {open ? (
        <Dialog open={true} maxWidth="md" onClose={() => setOpen(false)}>
          <DialogTitle>Anfrage ablehnen</DialogTitle>
          <DialogContent>
            <NewRangePicker
              small
              onChange={([startDate, endDate]) => {
                setStartDate(!!startDate ? startDate.format("YYYY-MM-DD") : null);
                setEndDate(!!endDate ? endDate.format("YYYY-MM-DD") : null);
              }}
              value={[dayjs(startDate, "YYYY-MM-DD"), dayjs(endDate, "YYYY-MM-DD")]}
            />
            <TextField
              sx={{ mt: 1 }}
              size="small"
              fullWidth
              multiline
              value={comment}
              placeholder="Begründung"
              onChange={(e) => setComment((e.target as HTMLInputElement).value)}
            />
            <LabelWrapper label={"Mail senden?"}>
              <ToggleButtonGroup exclusive value={sendMail} onChange={(_, status) => setSendMail(status)}>
                <ToggleButton color="success" value={true}>
                  Ja
                </ToggleButton>
                <ToggleButton color="error" value={false}>
                  Nein
                </ToggleButton>
              </ToggleButtonGroup>
            </LabelWrapper>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Abbrechen</Button>
            <LoadingButton
              variant="contained"
              color="error"
              loading={changeMutation.isPending}
              onClick={() => {
                setWaitForSuccess(true);
                const referenceKey = referenceType === "CREWS" ? "crew" : referenceType === "STAFF" ? "staffMember" : "resource";
                changeMutation.mutate({
                  rejectOrder: {
                    order: orderId,
                    ...(referenceId > 0 ? { [referenceKey]: referenceId } : {}),
                    startDate,
                    endDate,
                    sendMail,
                    comment,
                  },
                });
              }}
            >
              Ablehnen
            </LoadingButton>
          </DialogActions>
        </Dialog>
      ) : null}
    </Fragment>
  );
};

const Information = ({ data, did, today }) => {
  const isCritical = (data?.materialOrders || []).some((x) => x?.critical || x?.plantClosed === today);
  const downtimes = getActiveDowntimes(data?.phase?.downtimes, today);
  const downtime = downtimes.length > 0;
  const downtimeComment = downtimes.map((item) => item.comment).join("; ");
  const plantComment = isCritical
    ? (data?.materialOrders || [])
        .flatMap((x) => (x.plantComment.length > 0 ? [x.plantComment] : x?.plantClosed === today ? ["Schließtag"] : []))
        .join("; ")
    : "";
  return (
    <Fragment>
      <div
        className={
          "vc-res-status color " +
          (downtime
            ? "orange"
            : isCritical
            ? "red"
            : !!data.orderSentAt
              ? "darkgreen"
              : data.status === "PLANNING"
                ? "blue"
                : data.status === "RAW"
                  ? "yellow"
                  : data.status === "READY"
                    ? "orange"
                    : "green")
        }
      />
      <div className={"infocomp" + (isCritical ? " critical" : "")} style={downtime ? { backgroundColor: "#fff3e0" } : undefined}>
        <CalendarProjectName project={data.phase.project}>
          {" "}<span className="forcenotbold">{data.phase.name}</span>
        </CalendarProjectName>
        {isCritical && plantComment.length ? (
          <div className="info">
            <Warning fontSize="inherit" />
            <div className="txt">KRITISCH: {plantComment}</div>
          </div>
        ) : null}
        {downtime ? <div className="info" style={{ color: "#b45309" }}>
          <Warning fontSize="inherit" />
          <div className="txt">Stillstand: {downtimeComment}</div>
        </div> : null}
        {!!data.resourceBooking && !!data.resourceBooking.driver ? (
          <div className="info">
            <AccountCircle fontSize="inherit" />
            <div className="txt">{data.resourceBooking.driver.name}</div>
          </div>
        ) : null}
        {!!data.resourceBooking && !!data.resourceBooking.driverOf ? (
          <div className="info">
            <LocalShipping fontSize="inherit" />
            <div className="txt">{data.resourceBooking.driverOf.name + " (" + data.resourceBooking.driverOf.number + ")"}</div>
          </div>
        ) : null}

        {!!data.resourceOrder ? (
          <Fragment>
            {!!data.orderSentBy ? (
              <div className="info">
                <Call fontSize="inherit" />
                <div className="txt">{data.orderSentBy.firstName + " " + data.orderSentBy.name}</div>
              </div>
            ) : null}
            {data.resourceOrder.comment.length > 0 ? (
              <div className="info">
                <ChatBubble fontSize="inherit" />
                <div className="txt">{data.resourceOrder.comment}</div>
              </div>
            ) : null}
          </Fragment>
        ) : did in data.crews && Object.keys(data.crews).length === 1 ? null : (
          <Fragment>
            <div className="info">
              <PeopleAlt fontSize="inherit" />
              <div className="txt">{Object.values(data.crews).join("; ")}</div>
            </div>
          </Fragment>
        )}
      </div>
    </Fragment>
  );
};

export default function ResInner({ elements, did, i, readOnly, changeMutation, setJobInfoId, deleteMutation, type }) {
  const navigate = useNavigate();
  const dates = Object.keys(elements).sort();
  const date = dates[0];
  const element = !!elements[date][i] ? elements[date][i].element : null;

  if (!element) return null;

  return (
    <Fragment>
      <Information data={element} did={did} today={date} />
      {!!changeMutation ? (
        <div className="resOrderEdit">
          <div className="resOrderEditTopPart">
            {!readOnly && !!element.resourceOrder && !element.resourceOrder.singleDayOrder ? (
              did !== 0 ? (
                <div
                  onClick={() =>
                    changeMutation.mutate({
                      move: null,
                      orderToBooking: {
                        order: element.resourceOrder.id,
                        startDate: date,
                        jobId: null,
                        target: did,
                      },
                      bookingMove: null,
                    })
                  }
                  className="resOrderButton green"
                >
                  <DoneAll fontSize="inherit" />
                  {!!element?.resourceOrder?.endDate ? <div className="until">&rarr;{element?.resourceOrder?.endDate}</div> : null}
                </div>
              ) : !!element?.resourceOrder?.endDate ? (
                <div className="until2">&rarr;{element?.resourceOrder?.endDate}</div>
              ) : null
            ) : null}
          </div>
          <div className="resOrderEditBottomPart">
            {dates.map((datex) =>
              !!elements[datex][i] && !!elements[datex][i].element ? (
                <div className="resOrderEditDatePart" key={datex}>
                  <div className="resOrderButton" onClick={() => setJobInfoId(elements[datex][i].element.id)}>
                    <Search fontSize="inherit" />
                  </div>
                  {!readOnly && !!elements[datex][i].element.resourceBooking ? (
                    <Deleter
                      date={elements[datex][i].element.date}
                      bookingId={elements[datex][i].element.resourceBooking.id}
                      deleteMutation={deleteMutation}
                    />
                  ) : !readOnly && did !== 0 && !!elements[datex][i].element.resourceOrder ? (
                    <div
                      className="resOrderButton green"
                      onClick={() =>
                        changeMutation.mutate({
                          move: null,
                          orderToBooking: {
                            order: elements[datex][i].element.resourceOrder.id,
                            jobId: elements[datex][i].element.id,
                            target: did,
                          },
                          bookingMove: null,
                        })
                      }
                    >
                      <Check fontSize="inherit" />
                    </div>
                  ) : null}
                  {!readOnly && !!elements[datex][i].element.resourceOrder ? (
                    <Rejector
                      date={elements[datex][i].element.date}
                      changeMutation={changeMutation}
                      orderId={elements[datex][i].element.resourceOrder.id}
                      referenceType={type}
                      referenceId={did}
                    />
                  ) : null}
                </div>
              ) : null,
            )}
          </div>
        </div>
      ) : (
        false
      )}
    </Fragment>
  );
}
